import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  IconButton, 
  TextField, 
  List,
  ListItem,
  Typography
} from '@mui/material';
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Link as LinkIcon,
  OpenInNew as OpenInNewIcon 
} from '@mui/icons-material';
import { getPlayLogLinks, updatePlayLogLinks, PlayLogLink } from '../../services/api';
import Dialog from '../dialogs/Dialog';

interface LinkManagerProps {
  gameId: number;
  showHeader?: boolean;
}

const LinkManager: React.FC<LinkManagerProps> = ({ gameId, showHeader = false }) => {
  const [links, setLinks] = useState<PlayLogLink[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formTitle, setFormTitle] = useState('');
  const [formUrl, setFormUrl] = useState('');

  // URLのバリデーション
  const isValidUrl = (url: string) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // URLを正規化（httpプロトコルがない場合は追加）
  const normalizeUrl = (url: string) => {
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return `https://${url}`;
    }
    return url;
  };

  // リンクを取得
  const fetchLinks = async () => {
    try {
      const response = await getPlayLogLinks(gameId);
      setLinks(response.data.data || []);
    } catch (error) {
      console.error('リンク取得エラー:', error);
      setLinks([]);
    }
  };

  // リンクを保存
  const saveLinks = async (newLinks: PlayLogLink[]) => {
    try {
      await updatePlayLogLinks(gameId, newLinks);
      setLinks(newLinks);
    } catch (error) {
      console.error('リンク保存エラー:', error);
    }
  };

  useEffect(() => {
    fetchLinks();
  }, [gameId]);

  const handleAddLink = () => {
    if (links.length >= 3) {
      alert('リンクは3つまでしか登録できません');
      return;
    }
    setEditingIndex(null);
    setFormTitle('');
    setFormUrl('');
    setDialogOpen(true);
  };

  const handleEditLink = (index: number) => {
    setEditingIndex(index);
    setFormTitle(links[index].title);
    setFormUrl(links[index].url);
    setDialogOpen(true);
  };

  const handleDeleteLink = (index: number) => {
    const newLinks = links.filter((_, i) => i !== index);
    saveLinks(newLinks);
  };

  const handleSaveLink = () => {
    if (!formTitle.trim() || !formUrl.trim()) return;
    
    const normalizedUrl = normalizeUrl(formUrl.trim());
    if (!isValidUrl(normalizedUrl)) {
      alert('有効なURLを入力してください');
      return;
    }

    const newLink = { title: formTitle.trim(), url: normalizedUrl };
    const newLinks = editingIndex !== null 
      ? links.map((link, i) => i === editingIndex ? newLink : link)
      : [...links, newLink];
    
    saveLinks(newLinks);
    setDialogOpen(false);
  };

  const handleOpenLink = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
      {/* ヘッダー */}
      {showHeader && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" sx={{ 
            fontSize: '0.75rem', 
            color: 'text.secondary',
            fontWeight: 500 
          }}>
            リンクマネージャー
          </Typography>
          <Typography sx={{ 
            fontSize: '0.65rem', 
            color: 'text.secondary',
            opacity: 0.7
          }}>
            {links.length}/3
          </Typography>
        </Box>
      )}
      
      {/* リンクリスト */}
      {links.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ 
          textAlign: 'center', 
          py: 2,
          fontSize: '0.7rem'
        }}>
          リンクが登録されていません
        </Typography>
      ) : (
        <List dense sx={{ py: 0 }}>
          {links.map((link, index) => (
            <ListItem 
              key={index}
              sx={{ 
                px: 0, 
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.02)',
                borderRadius: 1,
                mb: 0.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                width: '100%',
                minWidth: 0
              }}>
                <LinkIcon sx={{ 
                  fontSize: '0.9rem', 
                  color: 'text.secondary', 
                  mr: 1,
                  flexShrink: 0
                }} />
                <Box sx={{ 
                  flex: 1, 
                  minWidth: 0,
                  overflow: 'hidden'
                }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: 'text.primary',
                      cursor: 'pointer',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        color: '#d9ff00'
                      }
                    }}
                    onClick={() => handleOpenLink(link.url)}
                  >
                    {link.title}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      fontSize: '0.65rem',
                      color: 'text.secondary',
                      display: 'block',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {link.url}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', ml: 1 }}>
                  <IconButton 
                    size="small" 
                    onClick={() => handleOpenLink(link.url)}
                    sx={{ p: 0.25, mr: 0.5 }}
                  >
                    <OpenInNewIcon sx={{ fontSize: '0.8rem' }} />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleEditLink(index)}
                    sx={{ p: 0.25, mr: 0.5 }}
                  >
                    <EditIcon sx={{ fontSize: '0.8rem' }} />
                  </IconButton>
                  <IconButton 
                    size="small" 
                    onClick={() => handleDeleteLink(index)}
                    sx={{ p: 0.25 }}
                  >
                    <DeleteIcon sx={{ fontSize: '0.8rem' }} />
                  </IconButton>
                </Box>
              </Box>
            </ListItem>
          ))}
        </List>
      )}

      {/* 追加ボタン */}
      <Button
        startIcon={<AddIcon />}
        onClick={handleAddLink}
        variant="outlined"
        size="small"
        fullWidth
        disabled={links.length >= 3}
        sx={{
          mt: 1,
          fontSize: '0.7rem',
          py: 0.75,
          borderColor: 'rgba(255, 255, 255, 0.2)',
          color: 'text.secondary',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.4)',
            backgroundColor: 'rgba(255, 255, 255, 0.05)'
          },
          '&:disabled': {
            opacity: 1,
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.3)'
          }
        }}
      >
        {links.length >= 3 ? 'リンク上限' : 'リンクを追加'}
      </Button>

      {/* 編集ダイアログ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editingIndex !== null ? 'リンクを編集' : 'リンクを追加'}
        showCloseButton={false}
        content={
          <Box>
            <TextField
              autoFocus
              margin="dense"
              label="タイトル"
              fullWidth
              size="small"
              variant="outlined"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="例: 攻略Wiki"
              sx={{ mb: 2 }}
            />
            <TextField
              margin="dense"
              label="URL"
              fullWidth
              size="small"
              variant="outlined"
              value={formUrl}
              onChange={(e) => setFormUrl(e.target.value)}
              placeholder="例: https://example.com"
              helperText="http://またはhttps://で始まるURLを入力してください"
              sx={{ mb: 2 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setDialogOpen(false)}>
                キャンセル
              </Button>
              <Button 
                onClick={handleSaveLink}
                variant="contained"
                disabled={!formTitle.trim() || !formUrl.trim()}
              >
                {editingIndex !== null ? '更新' : '追加'}
              </Button>
            </Box>
          </Box>
        }
      />
    </Box>
  );
};

export default LinkManager;